import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import {
  usePayrollConfig,
  useSavePayrollConfig,
  useBizPayrollConfig,
  useSaveBizPayrollConfig,
} from "../../../hooks/usePayroll";
import { useStores } from "../../../hooks/useStore";
import type { DynamicDeduction, DynamicAllowance, TaxBracket, TaxConfig, PayrollConfig, DeductionFrequency } from "../../../lib/payrollApi";
import {
  useBonusConfigs,
  useCreateBonusConfig,
  useUpdateBonusConfig,
  useDeleteBonusConfig,
  type BonusConfig,
} from "../../../hooks/useBonusConfig";
import type { BonusPayoutPeriod } from "../../../lib/bonusConfigApi";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** When provided the modal uses business-scoped /biz/{businessId}/payroll/config endpoints. */
  businessId?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

const DEFAULT_CONFIG: Omit<PayrollConfig, "updatedAt" | "updatedBy"> = {
  govDeductions: [
    // value = employee's own contribution; employerValue = employer's separate share
    { id: "sss", name: "SSS Contribution", type: "FIXED", value: 1350, employerValue: 900, frequency: "MONTHLY", enabled: true },
    { id: "philhealth", name: "PhilHealth", type: "PERCENTAGE", value: 2.5, employerValue: 2.5, frequency: "MONTHLY", enabled: true },
    { id: "pagibig", name: "Pag-IBIG", type: "FIXED", value: 100, employerValue: 100, frequency: "MONTHLY", enabled: true },
  ],
  allowances: [
    { id: "transport", name: "Transportation Allowance", amount: 1500, enabled: true },
    { id: "rice", name: "Rice Allowance", amount: 1000, enabled: true },
  ],
  storeCommissions: {},
};

const DEFAULT_TAX_CONFIG: TaxConfig = {
  enabled: false,
  label: "Withholding Tax",
  brackets: [
    { id: "tax1", minIncome: 0, maxIncome: 20000, rate: 0 },
    { id: "tax2", minIncome: 20000, maxIncome: 33333, rate: 15 },
    { id: "tax3", minIncome: 33333, rate: 20 },
  ],
};

// ── Deduction Row ─────────────────────────────────────────────────────────────

const UNIT_SUFFIX = { PERCENTAGE: "%", FIXED: "₱" } as const;

function ValueInput({
  type,
  value,
  onChange,
  placeholder,
}: {
  type: DynamicDeduction["type"];
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative w-28 shrink-0">
      {type === "FIXED" && (
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
      )}
      <input
        type="number"
        min={0}
        step={type === "PERCENTAGE" ? 0.1 : 1}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        placeholder={placeholder}
        className={`w-full rounded-lg border border-[#e2e8f0] bg-white py-1.5 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] ${
          type === "FIXED" ? "pl-6 pr-2.5" : "px-2.5 pr-6"
        }`}
      />
      {type === "PERCENTAGE" && (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">%</span>
      )}
    </div>
  );
}

function FrequencyToggle({
  value,
  onChange,
}: {
  value: DeductionFrequency;
  onChange: (f: DeductionFrequency) => void;
}) {
  return (
    <div
      className="flex rounded-lg border border-[#e2e8f0] overflow-hidden text-xs font-semibold shrink-0"
      title="When to apply: once per cut-off vs split from a monthly amount"
    >
      <button
        type="button"
        onClick={() => onChange("PER_PERIOD")}
        className={`px-2 py-1.5 transition-colors ${
          value === "PER_PERIOD"
            ? "bg-[#2447d9] text-white"
            : "bg-white text-[#64748b] hover:bg-[#f8fafc]"
        }`}
        title="Deduct this amount every cut-off"
      >
        /cut-off
      </button>
      <button
        type="button"
        onClick={() => onChange("MONTHLY")}
        className={`px-2 py-1.5 transition-colors ${
          value === "MONTHLY"
            ? "bg-[#2447d9] text-white"
            : "bg-white text-[#64748b] hover:bg-[#f8fafc]"
        }`}
        title="Value is the monthly amount — split equally across the two cut-offs"
      >
        /month
      </button>
    </div>
  );
}

function DeductionRow({
  item,
  onChange,
  onRemove,
}: {
  item: DynamicDeduction;
  onChange: (updated: DynamicDeduction) => void;
  onRemove: () => void;
}) {
  const freq: DeductionFrequency = item.frequency ?? "PER_PERIOD";
  const empShare = item.employerValue ?? 0;
  const unit = UNIT_SUFFIX[item.type];

  // item.value is the employee's own contribution; compute what gets deducted per cut-off.
  const employeeNetPerPeriod = (() => {
    if (item.type === "FIXED") return freq === "MONTHLY" ? item.value / 2 : item.value;
    return null; // ₱ amount depends on salary; can't show without knowing it
  })();

  const employeeHint = employeeNetPerPeriod !== null && item.value > 0
    ? `Deducts: ₱${employeeNetPerPeriod.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/cut-off`
    : item.type === "PERCENTAGE" && item.value > 0
      ? `Deducts: ${item.value.toFixed(2)}% of semi-monthly pay`
      : null;

  return (
    <div className="rounded-lg border border-[#e2e8f0] bg-white p-2 space-y-2">
      {/* Main row */}
      <div className="flex items-center gap-2">
        {/* Enable toggle */}
        <button
          type="button"
          onClick={() => onChange({ ...item, enabled: !item.enabled })}
          className={`w-8 h-8 shrink-0 rounded-lg border-2 flex items-center justify-center transition-colors ${
            item.enabled
              ? "border-[#2447d9] bg-[#2447d9] text-white"
              : "border-[#e2e8f0] bg-white text-transparent"
          }`}
          title={item.enabled ? "Disable" : "Enable"}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
          </svg>
        </button>

        {/* Name */}
        <input
          type="text"
          value={item.name}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
          placeholder="Deduction name"
          className="flex-1 min-w-0 rounded-lg border border-[#e2e8f0] bg-white px-2.5 py-1.5 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
        />

        {/* % / ₱ type toggle */}
        <div className="flex rounded-lg border border-[#e2e8f0] overflow-hidden text-xs font-semibold shrink-0">
          {(["PERCENTAGE", "FIXED"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onChange({ ...item, type: t })}
              className={`px-2.5 py-1.5 transition-colors ${
                item.type === t ? "bg-[#2447d9] text-white" : "bg-white text-[#64748b] hover:bg-[#f8fafc]"
              }`}
            >
              {t === "PERCENTAGE" ? "%" : "₱"}
            </button>
          ))}
        </div>

        {/* Employee's contribution amount/rate */}
        <div className="flex flex-col shrink-0">
          <span className="text-[10px] text-[#94a3b8] mb-0.5 text-center">Employee</span>
          <ValueInput
            type={item.type}
            value={item.value}
            onChange={(v) => onChange({ ...item, value: v })}
          />
        </div>

        {/* Frequency toggle */}
        <FrequencyToggle value={freq} onChange={(f) => onChange({ ...item, frequency: f })} />

        {/* Remove */}
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 rounded-lg text-[#94a3b8] hover:text-[#dc2626] hover:bg-[#fef2f2] transition-colors shrink-0"
          title="Remove"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Sub-row: employer share + employee net hint */}
      <div className="flex items-center gap-3 pl-10">
        <span className="text-xs text-[#94a3b8] shrink-0">Employer pays:</span>
        <ValueInput
          type={item.type}
          value={empShare}
          onChange={(v) => onChange({ ...item, employerValue: v })}
        />
        {employeeHint ? (
          <span className="text-[10px] text-[#16a34a] font-semibold">{employeeHint}</span>
        ) : (
          <span className="text-[10px] text-[#94a3b8]">
            {freq === "MONTHLY" ? `${unit}/month ÷ 2 per cut-off` : `${unit} each cut-off`}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Allowance Row ─────────────────────────────────────────────────────────────

function AllowanceRow({
  item,
  onChange,
  onRemove,
}: {
  item: DynamicAllowance;
  onChange: (updated: DynamicAllowance) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange({ ...item, enabled: !item.enabled })}
        className={`w-8 h-8 shrink-0 rounded-lg border-2 flex items-center justify-center transition-colors ${
          item.enabled
            ? "border-[#2447d9] bg-[#2447d9] text-white"
            : "border-[#e2e8f0] bg-white text-transparent"
        }`}
        title={item.enabled ? "Disable" : "Enable"}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
        </svg>
      </button>
      <input
        type="text"
        value={item.name}
        onChange={(e) => onChange({ ...item, name: e.target.value })}
        placeholder="Allowance name"
        className="flex-1 min-w-0 rounded-lg border border-[#e2e8f0] bg-white px-2.5 py-1.5 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
      />
      <div className="relative w-32 shrink-0">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
        <input
          type="number"
          min={0}
          value={item.amount}
          onChange={(e) => onChange({ ...item, amount: parseFloat(e.target.value) || 0 })}
          className="w-full rounded-lg border border-[#e2e8f0] bg-white pl-6 pr-2.5 py-1.5 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 rounded-lg text-[#94a3b8] hover:text-[#dc2626] hover:bg-[#fef2f2] transition-colors shrink-0"
        title="Remove"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Tax Bracket Row ───────────────────────────────────────────────────────────

function TaxBracketRow({
  bracket,
  index,
  isLast,
  onChange,
  onRemove,
}: {
  bracket: TaxBracket;
  index: number;
  isLast: boolean;
  onChange: (updated: TaxBracket) => void;
  onRemove: () => void;
}) {
  const inputCls =
    "w-full rounded-lg border border-[#e2e8f0] bg-white px-2.5 py-1.5 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]";
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-[#94a3b8] shrink-0 w-4 text-right">{index + 1}.</span>
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        {/* Min income */}
        <div className="relative w-28 shrink-0">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[#94a3b8]">₱</span>
          <input
            type="number"
            min={0}
            step={1000}
            value={bracket.minIncome}
            onChange={(e) => onChange({ ...bracket, minIncome: parseFloat(e.target.value) || 0 })}
            className={`${inputCls} pl-5`}
            placeholder="Min"
          />
        </div>
        <span className="text-xs text-[#94a3b8] shrink-0">to</span>
        {/* Max income */}
        <div className="relative w-28 shrink-0">
          {!isLast && (
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[#94a3b8]">₱</span>
          )}
          <input
            type="number"
            min={0}
            step={1000}
            value={isLast ? "" : (bracket.maxIncome ?? "")}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              onChange({ ...bracket, maxIncome: isNaN(v) ? undefined : v });
            }}
            disabled={isLast}
            className={`${inputCls} ${isLast ? "bg-[#f8fafc] text-[#94a3b8] cursor-not-allowed pl-2.5" : "pl-5"}`}
            placeholder={isLast ? "No limit" : "Max"}
          />
        </div>
        <span className="text-xs text-[#94a3b8] shrink-0">→</span>
        {/* Rate */}
        <div className="relative w-24 shrink-0">
          <input
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={bracket.rate}
            onChange={(e) => onChange({ ...bracket, rate: parseFloat(e.target.value) || 0 })}
            className={`${inputCls} pr-6`}
            placeholder="Rate"
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-[#94a3b8]">%</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 rounded-lg text-[#94a3b8] hover:text-[#dc2626] hover:bg-[#fef2f2] transition-colors shrink-0"
        title="Remove bracket"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────

export function PayrollConfigModal({ isOpen, onClose, businessId }: Props) {
  const { toasts, toast, dismissToast } = useToast();
  const useBizScoped = !!businessId;

  // Always call both hooks; enable only the relevant one (React hooks rules).
  const adminConfigQuery = usePayrollConfig({ enabled: !useBizScoped });
  const bizConfigQuery = useBizPayrollConfig(businessId ?? "");
  const { data: existingConfig, isLoading: configLoading } = useBizScoped
    ? bizConfigQuery
    : adminConfigQuery;

  const adminSaveConfig = useSavePayrollConfig();
  const bizSaveConfig = useSaveBizPayrollConfig(businessId ?? "");
  const saveConfig = useBizScoped ? bizSaveConfig : adminSaveConfig;

  const { data: stores = [], isLoading: storesLoading } = useStores();

  const [govDeductions, setGovDeductions] = useState<DynamicDeduction[]>(DEFAULT_CONFIG.govDeductions);
  const [allowances, setAllowances] = useState<DynamicAllowance[]>(DEFAULT_CONFIG.allowances);
  const [storeCommissions, setStoreCommissions] = useState<Record<string, number>>({});
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);
  // Kept as a string so an empty field means "use schedule-based working days".
  const [workingDaysPerMonth, setWorkingDaysPerMonth] = useState<string>("");
  const [overtimeMultiplier, setOvertimeMultiplier] = useState<string>("1.25");
  const [regularHolidayMultiplier, setRegularHolidayMultiplier] = useState<string>("2.0");
  const [specialHolidayMultiplier, setSpecialHolidayMultiplier] = useState<string>("1.3");
  const [holidayOTMultiplier, setHolidayOTMultiplier] = useState<string>("0.30");

  useEffect(() => {
    if (!isOpen) return;
    if (existingConfig) {
      setGovDeductions(existingConfig.govDeductions);
      setAllowances(existingConfig.allowances);
      setStoreCommissions(existingConfig.storeCommissions ?? {});
      setWorkingDaysPerMonth(
        existingConfig.workingDaysPerMonth ? String(existingConfig.workingDaysPerMonth) : "",
      );
      setOvertimeMultiplier(String(existingConfig.overtimeMultiplier ?? 1.25));
      setRegularHolidayMultiplier(String(existingConfig.regularHolidayMultiplier ?? 2.0));
      setSpecialHolidayMultiplier(String(existingConfig.specialHolidayMultiplier ?? 1.3));
      setHolidayOTMultiplier(String(existingConfig.holidayOTMultiplier ?? 0.30));
      const loadedTax = existingConfig.taxConfig ?? DEFAULT_TAX_CONFIG;
      // Ensure the last bracket is always unbounded when loading from DB
      if (loadedTax.brackets.length > 0) {
        const copy = [...loadedTax.brackets];
        copy[copy.length - 1] = { ...copy[copy.length - 1], maxIncome: undefined };
        setTaxConfig({ ...loadedTax, brackets: copy });
      } else {
        setTaxConfig(loadedTax);
      }
    } else {
      setGovDeductions(DEFAULT_CONFIG.govDeductions);
      setAllowances(DEFAULT_CONFIG.allowances);
      setStoreCommissions({});
      setTaxConfig(DEFAULT_TAX_CONFIG);
      setWorkingDaysPerMonth("");
      setOvertimeMultiplier("1.25");
      setRegularHolidayMultiplier("2.0");
      setSpecialHolidayMultiplier("1.3");
      setHolidayOTMultiplier("0.30");
    }
  }, [existingConfig, isOpen]);

  const addDeduction = () => {
    setGovDeductions((prev) => [
      ...prev,
      { id: newId(), name: "", type: "FIXED", value: 0, employerValue: 0, frequency: "MONTHLY", enabled: true },
    ]);
  };

  const addAllowance = () => {
    setAllowances((prev) => [
      ...prev,
      { id: newId(), name: "", amount: 0, enabled: true },
    ]);
  };

  const addTaxBracket = () => {
    setTaxConfig((prev) => ({
      ...prev,
      brackets: [...prev.brackets, { id: newId(), minIncome: 0, rate: 0 }],
    }));
  };

  const handleSave = async () => {
    const emptyDeduction = govDeductions.find((d) => !d.name.trim());
    const emptyAllowance = allowances.find((a) => !a.name.trim());
    if (emptyDeduction || emptyAllowance) {
      toast.error("All deduction and allowance entries must have a name.");
      return;
    }
    if (taxConfig.enabled && !taxConfig.label.trim()) {
      toast.error("Tax label is required when income tax is enabled.");
      return;
    }
    const parsedWdm = parseInt(workingDaysPerMonth, 10);
    const wdm =
      workingDaysPerMonth.trim() !== "" && !isNaN(parsedWdm) && parsedWdm > 0
        ? parsedWdm
        : undefined;
    try {
      // Omit workingDaysPerMonth when empty so saving clears any previous value
      // (config is stored via full-item Put).
      await saveConfig.mutateAsync({
        govDeductions,
        allowances,
        storeCommissions,
        taxConfig,
        ...(wdm !== undefined ? { workingDaysPerMonth: wdm } : {}),
        overtimeMultiplier: parseFloat(overtimeMultiplier) || 1.25,
        regularHolidayMultiplier: parseFloat(regularHolidayMultiplier) || 2.0,
        specialHolidayMultiplier: parseFloat(specialHolidayMultiplier) || 1.3,
        holidayOTMultiplier: parseFloat(holidayOTMultiplier) || 0.30,
      });
      toast.success("Payroll configuration saved.");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to save configuration. Please try again.");
    }
  };

  const commissionStores = stores.filter((s) => s.paymentType === "COMMISSION");
  const isLoading = configLoading || storesLoading;

  // ── Bonus configs state ───────────────────────────────────────────────────
  const bonusConfigsQuery = useBonusConfigs(businessId ?? "");
  const bonusConfigs = bonusConfigsQuery.data ?? [];
  const createBonus = useCreateBonusConfig(businessId ?? "");
  const updateBonus = useUpdateBonusConfig(businessId ?? "");
  const deleteBonus = useDeleteBonusConfig(businessId ?? "");

  const [bonusEditing, setBonusEditing] = useState<{
    bonusId?: string; // undefined = new
    name: string;
    payoutPeriods: BonusPayoutPeriod[];
    basisMonths: string;
    enabled: boolean;
  } | null>(null);

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  function toggleSlot(periods: BonusPayoutPeriod[], month: number, half: "H1" | "H2"): BonusPayoutPeriod[] {
    const exists = periods.some((p) => p.month === month && p.half === half);
    return exists
      ? periods.filter((p) => !(p.month === month && p.half === half))
      : [...periods, { month, half }].sort((a, b) => a.month - b.month || (a.half < b.half ? -1 : 1));
  }

  function slotLabel(p: BonusPayoutPeriod) {
    return `${MONTHS[p.month - 1]} ${p.half === "H1" ? "1–15" : "16–EOM"}`;
  }

  async function handleSaveBonus() {
    if (!bonusEditing) return;
    const bm = parseFloat(bonusEditing.basisMonths);
    if (!bonusEditing.name.trim()) { toast.error("Name is required."); return; }
    if (bonusEditing.payoutPeriods.length === 0) { toast.error("Select at least one payout period."); return; }
    if (isNaN(bm) || bm <= 0) { toast.error("Basis months must be a positive number."); return; }
    try {
      if (bonusEditing.bonusId) {
        await updateBonus.mutateAsync({ bonusId: bonusEditing.bonusId, payload: { name: bonusEditing.name, payoutPeriods: bonusEditing.payoutPeriods, basisMonths: bm, enabled: bonusEditing.enabled } });
      } else {
        await createBonus.mutateAsync({ name: bonusEditing.name, payoutPeriods: bonusEditing.payoutPeriods, basisMonths: bm, enabled: bonusEditing.enabled });
      }
      setBonusEditing(null);
      toast.success("Bonus configuration saved.");
    } catch {
      toast.error("Failed to save bonus configuration.");
    }
  }

  async function handleDeleteBonus(bonusId: string) {
    try {
      await deleteBonus.mutateAsync(bonusId);
      toast.success("Bonus configuration deleted.");
    } catch {
      toast.error("Failed to delete bonus configuration.");
    }
  }

  async function handleToggleBonusEnabled(config: BonusConfig) {
    try {
      await updateBonus.mutateAsync({ bonusId: config.bonusId, payload: { enabled: !config.enabled } });
    } catch {
      toast.error("Failed to update bonus.");
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Payroll Configuration" size="lg">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="space-y-6">

          {/* Gov Deductions */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                Government Mandated Deductions
              </h4>
              <button
                type="button"
                onClick={addDeduction}
                className="flex items-center gap-1 text-xs font-semibold text-[#2447d9] hover:text-[#1e3a8a] transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
            <p className="text-xs text-[#94a3b8] mb-3">
              Computed per semi-monthly period. Toggle to enable/disable each deduction.
            </p>
            {govDeductions.length === 0 ? (
              <p className="text-xs text-[#94a3b8] italic py-1">No deductions configured.</p>
            ) : (
              <div className="space-y-2">
                {govDeductions.map((d, i) => (
                  <DeductionRow
                    key={d.id}
                    item={d}
                    onChange={(updated) =>
                      setGovDeductions((prev) => prev.map((x, j) => (j === i ? updated : x)))
                    }
                    onRemove={() => setGovDeductions((prev) => prev.filter((_, j) => j !== i))}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Allowances */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                Standard Allowances
              </h4>
              <button
                type="button"
                onClick={addAllowance}
                className="flex items-center gap-1 text-xs font-semibold text-[#2447d9] hover:text-[#1e3a8a] transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
            <p className="text-xs text-[#94a3b8] mb-3">Fixed ₱ amount added per period.</p>
            {allowances.length === 0 ? (
              <p className="text-xs text-[#94a3b8] italic py-1">No allowances configured.</p>
            ) : (
              <div className="space-y-2">
                {allowances.map((a, i) => (
                  <AllowanceRow
                    key={a.id}
                    item={a}
                    onChange={(updated) =>
                      setAllowances((prev) => prev.map((x, j) => (j === i ? updated : x)))
                    }
                    onRemove={() => setAllowances((prev) => prev.filter((_, j) => j !== i))}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Absence Deduction basis */}
          <div>
            <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
              Absence Deduction
            </h4>
            <p className="text-xs text-[#94a3b8] mb-3">
              Working days in a month used to compute the daily rate for absences
              (daily rate = monthly salary ÷ working days per month). Leave blank to use
              each employee&apos;s schedule-based working days for the period.
            </p>
            <div className="flex items-center gap-3">
              <label className="text-xs text-[#64748b] shrink-0">Working days / month</label>
              <input
                type="number"
                min={1}
                max={31}
                step={1}
                value={workingDaysPerMonth}
                onChange={(e) => setWorkingDaysPerMonth(e.target.value)}
                placeholder="e.g. 26"
                className="w-28 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
              />
              {workingDaysPerMonth.trim() !== "" && (
                <button
                  type="button"
                  onClick={() => setWorkingDaysPerMonth("")}
                  className="text-xs text-[#94a3b8] hover:text-[#475569] transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Work Time Multipliers */}
          <div>
            <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
              Work Time Multipliers
            </h4>
            <p className="text-xs text-[#94a3b8] mb-3">
              Used to compute overtime and holiday pay for approved Work Time Claims. Defaults follow Philippine Labor Law.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Overtime (×)", value: overtimeMultiplier, set: setOvertimeMultiplier, hint: "Default: 1.25" },
                { label: "Regular Holiday (×)", value: regularHolidayMultiplier, set: setRegularHolidayMultiplier, hint: "Default: 2.0" },
                { label: "Special Non-Working (×)", value: specialHolidayMultiplier, set: setSpecialHolidayMultiplier, hint: "Default: 1.3" },
                { label: "Holiday OT Premium (×)", value: holidayOTMultiplier, set: setHolidayOTMultiplier, hint: "Default: 0.30" },
              ].map(({ label, value, set, hint }) => (
                <div key={label}>
                  <label className="block text-xs text-[#64748b] mb-1">{label}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={value}
                    onChange={(e) => set(e.target.value)}
                    className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
                  />
                  <p className="text-[10px] text-[#94a3b8] mt-0.5">{hint}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Income Tax */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  Income Tax
                </h4>
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[#f1f5f9] text-[#64748b]">
                  Optional
                </span>
              </div>
              <button
                type="button"
                onClick={() => setTaxConfig((prev) => ({ ...prev, enabled: !prev.enabled }))}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#2447d9] ${
                  taxConfig.enabled ? "bg-[#2447d9]" : "bg-[#e2e8f0]"
                }`}
                role="switch"
                aria-checked={taxConfig.enabled}
                title={taxConfig.enabled ? "Disable income tax" : "Enable income tax"}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                    taxConfig.enabled ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
            <p className="text-xs text-[#94a3b8] mb-3">
              When enabled, the employee&apos;s <strong>monthly salary</strong> is matched against the brackets below
              to determine the tax rate. The resulting tax is then deducted from their semi-monthly basic pay.
              Example: a ₱30,000/month employee matched to a 15% bracket gets ₱2,250 deducted each cut-off.
            </p>

            {taxConfig.enabled && (
              <div className="space-y-3 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-3">
                {/* Label */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[#64748b] shrink-0 w-24">Deduction label</label>
                  <input
                    type="text"
                    value={taxConfig.label}
                    onChange={(e) => setTaxConfig((prev) => ({ ...prev, label: e.target.value }))}
                    placeholder="e.g. Withholding Tax"
                    className="flex-1 rounded-lg border border-[#e2e8f0] bg-white px-2.5 py-1.5 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
                  />
                </div>

                {/* Bracket header */}
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wide">
                    Income Brackets (monthly salary)
                  </p>
                  <button
                    type="button"
                    onClick={addTaxBracket}
                    className="flex items-center gap-1 text-xs font-semibold text-[#2447d9] hover:text-[#1e3a8a] transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Bracket
                  </button>
                </div>

                {/* Column labels */}
                {taxConfig.brackets.length > 0 && (
                  <div className="flex items-center gap-2 px-0.5">
                    <span className="w-4" />
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <span className="w-28 text-[10px] text-[#94a3b8] font-medium shrink-0">Min income</span>
                      <span className="w-4" />
                      <span className="w-28 text-[10px] text-[#94a3b8] font-medium shrink-0">Max income</span>
                      <span className="w-4" />
                      <span className="w-24 text-[10px] text-[#94a3b8] font-medium shrink-0">Tax rate</span>
                    </div>
                    <span className="w-7" />
                  </div>
                )}

                {taxConfig.brackets.length === 0 ? (
                  <p className="text-xs text-[#94a3b8] italic py-1">
                    No brackets configured. Add at least one bracket.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {taxConfig.brackets.map((bracket, i) => (
                      <TaxBracketRow
                        key={bracket.id}
                        bracket={bracket}
                        index={i}
                        isLast={i === taxConfig.brackets.length - 1}
                        onChange={(updated) =>
                          setTaxConfig((prev) => ({
                            ...prev,
                            brackets: prev.brackets.map((b, j) => (j === i ? updated : b)),
                          }))
                        }
                        onRemove={() =>
                          setTaxConfig((prev) => {
                            const next = prev.brackets.filter((_, j) => j !== i);
                            // Always clear maxIncome on the new last bracket so it's truly unbounded
                            if (next.length > 0) {
                              const last = next.length - 1;
                              next[last] = { ...next[last], maxIncome: undefined };
                            }
                            return { ...prev, brackets: next };
                          })
                        }
                      />
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-[#94a3b8]">
                  Ranges are based on monthly salary. The last bracket has no upper limit. An income exactly at a min value falls into that bracket.
                </p>
              </div>
            )}
          </div>

          {/* Store Commission Rates — only for commission-type stores */}
          <div>
            <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
              Store Commission Rates
            </h4>
            <p className="text-xs text-[#94a3b8] mb-3">
              % of store revenue used as basic pay for commission-based stores.
              Only stores marked as &ldquo;Commission&rdquo; appear here.
            </p>
            {commissionStores.length === 0 ? (
              <p className="text-xs text-[#94a3b8] italic py-2">
                No commission-based stores. Set a store&apos;s payment type to &ldquo;Commission&rdquo; in Manage Store.
              </p>
            ) : (
              <div className="space-y-3">
                {commissionStores.map((store) => (
                  <div key={store.storeId} className="flex items-center gap-3">
                    <span className="flex-1 min-w-0 text-sm text-[#64748b] truncate">
                      {store.name}
                    </span>
                    <div className="relative w-28 shrink-0">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.1}
                        value={storeCommissions[store.storeId] ?? 0}
                        onChange={(e) =>
                          setStoreCommissions((prev) => ({
                            ...prev,
                            [store.storeId]: parseFloat(e.target.value) || 0,
                          }))
                        }
                        className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 pr-7 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">
                        %
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bonus Payouts — only visible in biz-scoped mode */}
          {businessId && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  Bonus Payouts
                </h4>
                <button
                  type="button"
                  onClick={() => setBonusEditing({ name: "", payoutPeriods: [], basisMonths: "1", enabled: true })}
                  className="flex items-center gap-1 text-xs font-semibold text-[#2447d9] hover:text-[#1e3a8a] transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Bonus
                </button>
              </div>
              <p className="text-xs text-[#94a3b8] mb-3">
                Configure 13th month, 14th month, or any recurring bonus. The system auto-computes the prorated amount per employee based on their hire date and salary when payroll is processed for a matching period.
              </p>

              {bonusConfigs.length === 0 && !bonusEditing && (
                <p className="text-xs text-[#94a3b8] italic py-1">No bonus payouts configured.</p>
              )}

              <div className="space-y-2">
                {bonusConfigs.map((bc) => (
                  <div key={bc.bonusId} className="rounded-lg border border-[#e2e8f0] bg-white p-3">
                    <div className="flex items-center gap-2">
                      {/* Enable toggle */}
                      <button
                        type="button"
                        onClick={() => handleToggleBonusEnabled(bc)}
                        className={`w-8 h-8 shrink-0 rounded-lg border-2 flex items-center justify-center transition-colors ${
                          bc.enabled
                            ? "border-[#2447d9] bg-[#2447d9] text-white"
                            : "border-[#e2e8f0] bg-white text-transparent"
                        }`}
                        title={bc.enabled ? "Disable" : "Enable"}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                        </svg>
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#0f172a] truncate">{bc.name}</p>
                        <p className="text-[10px] text-[#94a3b8]">
                          {bc.basisMonths}× monthly salary · paid in {bc.payoutPeriods.length} slot{bc.payoutPeriods.length !== 1 ? "s" : ""}:{" "}
                          {bc.payoutPeriods.map(slotLabel).join(", ")}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setBonusEditing({ bonusId: bc.bonusId, name: bc.name, payoutPeriods: bc.payoutPeriods, basisMonths: String(bc.basisMonths), enabled: bc.enabled })}
                        className="text-xs font-semibold text-[#2447d9] hover:text-[#1e3a8a] transition-colors px-2 py-1 rounded-lg hover:bg-[#eff3ff] shrink-0"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteBonus(bc.bonusId)}
                        className="p-1.5 rounded-lg text-[#94a3b8] hover:text-[#dc2626] hover:bg-[#fef2f2] transition-colors shrink-0"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Inline edit / create form */}
                {bonusEditing && (
                  <div className="rounded-lg border border-[#2447d9] bg-[#f8faff] p-3 space-y-3">
                    <p className="text-xs font-semibold text-[#2447d9]">
                      {bonusEditing.bonusId ? "Edit Bonus" : "New Bonus"}
                    </p>

                    {/* Name */}
                    <div>
                      <label className="block text-xs text-[#64748b] mb-1">Name</label>
                      <input
                        type="text"
                        placeholder="e.g. 13th Month Pay"
                        value={bonusEditing.name}
                        onChange={(e) => setBonusEditing((p) => p && ({ ...p, name: e.target.value }))}
                        className="w-full rounded-lg border border-[#e2e8f0] bg-white px-2.5 py-1.5 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
                      />
                    </div>

                    {/* Basis months */}
                    <div>
                      <label className="block text-xs text-[#64748b] mb-1">
                        Basis <span className="text-[#94a3b8]">(how many months' salary equals this bonus)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0.1"
                          step="0.5"
                          value={bonusEditing.basisMonths}
                          onChange={(e) => setBonusEditing((p) => p && ({ ...p, basisMonths: e.target.value }))}
                          className="w-24 rounded-lg border border-[#e2e8f0] bg-white px-2.5 py-1.5 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
                        />
                        <span className="text-xs text-[#64748b]">× monthly salary</span>
                      </div>
                    </div>

                    {/* Payout period picker */}
                    <div>
                      <label className="block text-xs text-[#64748b] mb-2">
                        Payout periods <span className="text-[#94a3b8]">(select one or more — the total bonus is split equally)</span>
                      </label>
                      <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto pr-1">
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) =>
                          (["H1", "H2"] as const).map((half) => {
                            const active = bonusEditing.payoutPeriods.some((p) => p.month === month && p.half === half);
                            return (
                              <button
                                key={`${month}-${half}`}
                                type="button"
                                onClick={() => setBonusEditing((p) => p && ({ ...p, payoutPeriods: toggleSlot(p.payoutPeriods, month, half) }))}
                                className={`text-xs py-1.5 px-2 rounded-lg border transition-colors text-left ${
                                  active
                                    ? "border-[#2447d9] bg-[#eff3ff] text-[#2447d9] font-semibold"
                                    : "border-[#e2e8f0] bg-white text-[#64748b] hover:bg-[#f8fafc]"
                                }`}
                              >
                                {MONTHS[month - 1]} {half === "H1" ? "1–15" : "16–EOM"}
                              </button>
                            );
                          })
                        )}
                      </div>
                      {bonusEditing.payoutPeriods.length > 0 && (
                        <p className="text-[10px] text-[#16a34a] mt-1.5 font-semibold">
                          Selected: {bonusEditing.payoutPeriods.map(slotLabel).join(", ")}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setBonusEditing(null)}
                        className="flex-1 rounded-lg border border-[#e2e8f0] bg-white py-1.5 text-xs font-semibold text-[#64748b] hover:bg-[#f8fafc] transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveBonus}
                        disabled={createBonus.isPending || updateBonus.isPending}
                        className="flex-1 rounded-lg bg-[#2447d9] text-white py-1.5 text-xs font-semibold hover:bg-[#1e3a8a] transition-colors disabled:opacity-50"
                      >
                        {createBonus.isPending || updateBonus.isPending ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2 border-t border-[#e2e8f0]">
            <Button variant="secondary" onClick={onClose} fullWidth>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              fullWidth
              disabled={saveConfig.isPending}
            >
              {saveConfig.isPending ? "Saving…" : "Save Configuration"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
